import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import UniversalChip from '@/components/UI/UniversalChip/UniversalChip';
import { chipTypes } from '@/helpers/constants';
import {
  getCampaignVehicles, getCampaignInfo, getCampaignVehiclesVolumes, getVolumeHeaderPlants, getAffectedVehiclesValidations,
  getCampaignValidations, getSelectedPlants, getCampaignSearch,
} from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { AffectedVehicles, CampaignSearch, LimitationSerie, ObjLimitationPlant, Validations, Volume, WKZNamePlant } from '@/types';
import { isViaModelSeries, isViaPlants } from '../../SeriesPanel/SeriesRow/series-row-utils';
import styles from '../../AffectedVehicles.module.scss';

type VolumeHeaderProps = {
  affectedVehicles: AffectedVehicles;
  validations: Validations;
  headerPlants: WKZNamePlant[];
  series: LimitationSerie[];
  volumes: Volume[];
  affectedVehiclesValidations: any;
  headerLanguage: string;
  plants: ObjLimitationPlant[];
  setShowCopyData: (value: boolean) => void;
  setManualEntry: (value: boolean) => void;
  plantSelected: WKZNamePlant;
  setPlantSelected: (plant: WKZNamePlant) => void;
  campaignSearch: CampaignSearch;
};

const VolumeHeader: React.FC<VolumeHeaderProps> = (props) => {
  const { affectedVehicles, validations, headerPlants, series, volumes, affectedVehiclesValidations, headerLanguage, plants,
    setShowCopyData, setManualEntry, plantSelected, setPlantSelected, campaignSearch } = props;

  const plantsToUse = useMemo(() => (
    [...(isViaPlants(affectedVehicles) ? plants : headerPlants)]
      ?.sort((a, b) => (a?.plantName?.toLowerCase() < b?.plantName?.toLowerCase() ? -1 : 1))),
  [affectedVehicles, plants, headerPlants]); // headerPlants -> via modelseries

  const getVolume = (plant = plantsToUse[0]) => (
    volumes?.find((currentVolume) => currentVolume.plant === plant.wkzNumber)
  );

  const plantExists = headerPlants.some(({ wkzNumber }) => wkzNumber === plantSelected?.wkzNumber);

  useEffect(() => {
    let plantAffected: WKZNamePlant;

    if (plantExists && plantSelected) {
      plantAffected = plantSelected;
    } else {
      plantAffected = isViaPlants(affectedVehicles) ? plants[0] : headerPlants[0];
    }

    setPlantSelected(plantAffected as WKZNamePlant);

    if (isViaPlants(affectedVehicles)) {
      setManualEntry(false);
    }
  }, [plants]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const volumesFound: Volume[] = [];
    let volumeFound: Volume | undefined;
    let plantAffected: WKZNamePlant;

    if (plantExists && plantSelected && isViaModelSeries(affectedVehicles)) {
      plantAffected = plantSelected;
    } else {
      plantAffected = isViaPlants(affectedVehicles) ? plants[0] : headerPlants[0];
    }

    setPlantSelected(plantAffected);

    if (plantAffected) {
      volumeFound = getVolume(plantAffected);

      if (volumeFound === undefined) {
        plantAffected = isViaPlants(affectedVehicles) ? plants[0] : headerPlants[0];
        setPlantSelected(plantAffected);
        volumeFound = getVolume(plantAffected);
      }
    }

    if (volumeFound && isViaModelSeries(affectedVehicles)) {
      setManualEntry(!volumeFound?.fileOptionSelected);
    }

    if (isViaModelSeries(affectedVehicles)) {
      headerPlants.forEach((hp) => {
        const volFound = getVolume(hp);

        if (volFound) {
          volumesFound.push(volFound as unknown as Volume);
        }
      });

      if (volumesFound.length === 0) {
        setManualEntry(false);
      }
    }
  }, [series]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectPlant = (plant) => {
    const volumeFound = getVolume(plant);

    if (isViaPlants(affectedVehicles)) {
      setManualEntry(false);
    } else {
      setManualEntry(!volumeFound?.fileOptionSelected);
      setShowCopyData(false);
    }
    setPlantSelected(plant);
  };

  useEffect(() => {
    if (campaignSearch?.fieldSelected && campaignSearch?.fieldSelected?.field?.path?.includes('limitationVolumeManualEntry')) {
      const fieldSelectedPathArray = campaignSearch?.fieldSelected?.field?.path?.split('.');
      const fieldSelectedPlant = plants.find((plant) => plant.wkzNumber === fieldSelectedPathArray[fieldSelectedPathArray.length - 1].replace('plant-', ''));
      selectPlant(fieldSelectedPlant);
    }
  }, [campaignSearch?.fieldSelected]);

  return (
    <div className={styles.HeaderContainer}>
      <div className={styles.PlantsContainer}>
        {plantsToUse && plantsToUse.map((option) => (
          option.plantName !== '' && (
            <UniversalChip
              key={option?.wkzNumber || 'new'}
              chipType={chipTypes.WITH_FUNCTION}
              active={option?.wkzNumber === plantSelected?.wkzNumber}
              error={
                Object.keys((affectedVehiclesValidations?.[headerLanguage]?.volumes || {})).length > 0
                && affectedVehiclesValidations?.[headerLanguage]?.volumes?.[option?.wkzNumber]?.invalid
                && typeof validations?.active === 'object' && validations?.active?.all
              }
              text={option?.plantName}
              onClick={(() => selectPlant(option))}
              noHighlightChip
            />
          )
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  headerPlants: getVolumeHeaderPlants(state) as unknown as WKZNamePlant[],
  volumes: getCampaignVehiclesVolumes(state),
  affectedVehicles: getCampaignVehicles(state),
  series: getCampaignVehicles(state)?.series || [],
  affectedVehiclesValidations: getAffectedVehiclesValidations(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  validations: getCampaignValidations(state),
  plants: getSelectedPlants(state) || [],
  campaignSearch: getCampaignSearch(state),
});

export default connect(mapStateToProps)(VolumeHeader);
